import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import EntityToggle from './EntityToggle';
import { useSidebar } from './SidebarContext';
import { isAdminLikeRole, isBackofficeRole, isSuperAdminRole } from '../../lib/roles';
import {
  Home,
  Building2,
  Calendar,
  FileText,
  Wallet,
  Users,
  Settings,
  LogOut,
  BarChart3,
  ClipboardList,
  Award,
  UserCheck,
  DollarSign,
  Banknote,
  Shield,
  FileStack,
  UsersRound,
  FileEdit,
  Mail,
  Bell,
  FileCheck,
  Target,
  TrendingUp,
  Layers,
  Gift,
  Image,
  Star,
  RefreshCw,
  Landmark,
  Sparkles,
  Activity,
  Calculator,
  Plane,
  X,
} from 'lucide-react';

const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { open, setOpen } = useSidebar();

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const closeOnNav = () => setOpen(false);

  const customerLinks = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/properties', label: 'Properties', icon: Building2 },
    { path: '/my-appointments', label: 'My Appointments', icon: Calendar },
    { path: '/my-bookings', label: 'My Bookings', icon: FileText },
    { path: '/loan-offers', label: 'Loan Offers', icon: Landmark },
    { path: '/surveys', label: 'My Surveys', icon: Star },
    { path: '/perks', label: 'Perks & Benefits', icon: Gift },
    { path: '/wallet', label: 'Wallet', icon: Wallet },
    { path: '/referrals', label: 'Referrals', icon: Users },
    { path: '/profile', label: 'Profile', icon: Settings },
  ];

  const agentLinks = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/agent-properties', label: 'Properties', icon: Building2 },
    { path: '/open-leads', label: 'Open Leads', icon: Target },
    { path: '/appointments', label: 'Appointments', icon: Calendar },
    { path: '/bookings', label: 'Bookings', icon: FileText },
    // V4.24.48 — Loan Cases entry for the agent role. Deep-links into the
    // same customer/agent flow surfaces we shipped in V4.24.45–47 so agents
    // can jump straight to any case they're assigned to.
    { path: '/loan-offers', label: 'Loan Cases', icon: Landmark },
    { path: '/commission-claims', label: 'Commission Claims', icon: DollarSign },
    { path: '/incentives', label: 'Incentives 2026', icon: Plane },
    { path: '/payment-slips', label: 'Payment Slips', icon: FileCheck },
    { path: '/perks', label: 'Perks & Benefits', icon: Gift },
    { path: '/wallet', label: 'Wallet', icon: Wallet },
    { path: '/my-team', label: 'My Team', icon: Users },
    { path: '/performance', label: 'Performance', icon: BarChart3 },
    { path: '/profile', label: 'Profile', icon: Settings },
  ];

  const adminLinks = [
    { path: '/admin', label: 'Dashboard', icon: Home },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/properties', label: 'Properties', icon: Building2 },
    { path: '/admin/categories', label: 'Categories', icon: Layers },
    { path: '/admin/units', label: 'Unit Availability', icon: ClipboardList },
    { path: '/admin/booking-templates', label: 'Booking Forms', icon: FileEdit },
    { path: '/admin/developer-templates', label: 'Developer PDFs', icon: FileCheck },
    { path: '/admin/pdf-templates', label: 'PDF Templates', icon: FileStack },
    { path: '/admin/email-templates', label: 'Email Templates', icon: Mail },
    { path: '/admin/push-notifications', label: 'Push Notifications', icon: Bell },
    { path: '/admin/agents', label: 'Agent Management', icon: Award },
    { path: '/admin/customers', label: 'Customer Management', icon: UserCheck },
    { path: '/admin/promotions', label: 'Auto-Promote', icon: TrendingUp },
    { path: '/admin/rank-config', label: 'Rank Config', icon: Target },
    { path: '/admin/commission-engine', label: 'Commission Engine', icon: Calculator, superAdminOnly: true },
    { path: '/admin/incentives', label: 'Incentives 2026', icon: Plane, superAdminOnly: true },
    { path: '/admin/kyc', label: 'KYC Approvals', icon: UserCheck },
    { path: '/admin/appointments', label: 'Appointments', icon: Calendar },
    { path: '/admin/bookings', label: 'Bookings', icon: FileText },
    { path: '/admin/payment-verification', label: 'Payment Verification', icon: FileCheck },
    { path: '/admin/commission-claims', label: 'Commission Claims', icon: ClipboardList },
    { path: '/admin/open-leads', label: 'Open Leads', icon: Target },
    { path: '/admin/withdrawals', label: 'Withdrawals', icon: Banknote },
    { path: '/admin/payouts/direct-bank', label: 'Direct Bank Payouts', icon: Banknote },
    { path: '/admin/legacy-commissions', label: 'Legacy Archive', icon: FileStack },
    { path: '/admin/config', label: 'Configuration', icon: Shield },
    { path: '/admin/perks', label: 'Perks & Benefits', icon: Gift },
    { path: '/admin/banners', label: 'Banners', icon: Image, superAdminOnly: true },
    { path: '/admin/refunds', label: 'Refund Management', icon: RefreshCw },
    { path: '/admin/loans', label: 'Loan Management', icon: Banknote },
    { path: '/admin/aicon-sync', label: 'AICon Sync', icon: Activity },
    { path: '/admin/team-members', label: 'Team Members', icon: UsersRound, superAdminOnly: true },
    { path: '/admin/ai-concierge', label: 'AI Concierge', icon: Sparkles, superAdminOnly: true },
    { path: '/admin/rbac', label: 'RBAC', icon: Shield, superAdminOnly: true },
  ];

  // PIC role - focused on claim review and approval
  const picLinks = [
    { path: '/admin', label: 'Dashboard', icon: Home },
    { path: '/admin/commission-claims', label: 'Commission Claims', icon: ClipboardList },
    { path: '/admin/bookings', label: 'Bookings', icon: FileText },
    { path: '/admin/refunds', label: 'Refund Management', icon: RefreshCw },
    { path: '/admin/properties', label: 'Properties', icon: Building2 },
    { path: '/profile', label: 'Profile', icon: Settings },
  ];

  // Account role - focused on payment verification, claim review and approval
  const accountLinks = [
    { path: '/admin', label: 'Dashboard', icon: Home },
    { path: '/admin/payment-verification', label: 'Payment Verification', icon: FileCheck },
    { path: '/admin/commission-claims', label: 'Commission Claims', icon: ClipboardList },
    { path: '/admin/bookings', label: 'Bookings', icon: FileText },
    { path: '/admin/refunds', label: 'Refund Management', icon: RefreshCw },
    { path: '/admin/withdrawals', label: 'Withdrawals', icon: Banknote },
    { path: '/admin/payouts/direct-bank', label: 'Direct Bank Payouts', icon: Banknote },
    { path: '/admin/loans', label: 'Loan Management', icon: Banknote },
    { path: '/admin/legacy-commissions', label: 'Legacy Archive', icon: FileStack },
    { path: '/profile', label: 'Profile', icon: Settings },
  ];

  // Determine which links to show based on role
  const getLinksForRole = (role) => {
    if (isAdminLikeRole(role)) return adminLinks;
    switch (role) {
      case 'pic':      return picLinks;
      case 'account':  return accountLinks;
      case 'agent':    return agentLinks;
      default:         return customerLinks;
    }
  };

  const allLinks = getLinksForRole(user?.role);
  const isSuperAdmin = isSuperAdminRole(user?.role) || (user?.role === 'admin' && user?.email === 'admin@samrumah.com');
  const links = allLinks.filter((l) => !l.superAdminOnly || isSuperAdmin);

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/40 z-40 transition-opacity duration-200 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setOpen(false)}
        data-testid="sidebar-overlay"
      />
      <aside
        className={`fixed left-0 top-0 h-screen w-64 lg:w-56 bg-white border-r border-gray-200 flex flex-col z-50 shadow-xl lg:shadow-sm transition-transform duration-300 ease-in-out
          ${open ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        data-testid="sidebar"
      >
      {/* Logo + Close (mobile) */}
      <div className="p-4 lg:p-5 border-b border-gray-100 flex items-center justify-between">
        <Link to="/" onClick={closeOnNav} className="flex items-center gap-2">
          <div className="w-9 h-9 bg-gradient-primary rounded-xl flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-gradient-brand">Sam Rumah</span>
        </Link>
        <button
          onClick={() => setOpen(false)}
          className="lg:hidden p-1 rounded-lg hover:bg-gray-100"
          aria-label="Close menu"
          data-testid="sidebar-close-btn"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#4E50FF]/10 flex items-center justify-center">
            <span className="text-[#4E50FF] font-semibold text-sm">
              {user?.full_name?.charAt(0)?.toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{user?.full_name}</p>
            <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
            {user?.agent_code && (
              <p className="text-xs text-[#4E50FF] font-mono font-medium">{user.agent_code}</p>
            )}
          </div>
        </div>
      </div>

      {/* Entity Toggle - Back-office only */}
      {isBackofficeRole(user?.role) && (
        <div className="px-3 py-2 border-b border-gray-100">
          <EntityToggle />
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {links.map((link) => {
          const Icon = link.icon;
          const active = isActive(link.path);
          return (
            <Link
              key={link.path}
              to={link.path}
              onClick={closeOnNav}
              data-testid={`nav-${link.path.replace(/\//g, '-')}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                link.indent ? 'ml-6 py-1.5 text-xs' : ''
              } ${
                active 
                  ? 'bg-[#4E50FF] text-white shadow-md shadow-[#4E50FF]/25' 
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{link.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-gray-100">
        <button
          onClick={() => { setOpen(false); logout(); }}
          data-testid="logout-btn"
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium w-full text-red-600 hover:bg-red-50 transition-all duration-200"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          <span>Logout</span>
        </button>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
